import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@marine/api-client/api';

// Types
export interface Module {
  id: string;
  name: string;
  display_name: string;
  description: string;
  icon: string;
  is_core: boolean;
  created_at: string;
  updated_at: string;
}

export interface TenantModule {
  id: string;
  tenant_id: string;
  module_id: string;
  is_enabled: boolean;
  configuration: Record<string, any> | null;
  enabled_at: string | null;
  disabled_at: string | null;
  module: Module;
}

// API Functions
export const modulesApi = {
  // Get all available modules
  getAllModules: (): Promise<Module[]> =>
    api.getList<Module>('/api/modules'),

  // Get tenant's enabled modules
  getTenantModules: (tenantId: string): Promise<TenantModule[]> =>
    api.getList<TenantModule>(`/api/tenants/${tenantId}/modules`),

  // Enable a module for a tenant
  enableModule: (tenantId: string, moduleId: string): Promise<TenantModule> =>
    api.postOne<TenantModule>(`/api/tenants/${tenantId}/modules/${moduleId}/enable`),

  // Disable a module for a tenant
  disableModule: (tenantId: string, moduleId: string): Promise<void> => {
    api.postAction(`/api/tenants/${tenantId}/modules/${moduleId}/disable`);
    return Promise.resolve();
  },

  // Configure a module
  configureModule: (
    tenantId: string,
    moduleId: string,
    configuration: Record<string, any>
  ): Promise<TenantModule> =>
    api.patchOne<TenantModule>(
      `/api/tenants/${tenantId}/modules/${moduleId}/configure`,
      { configuration }
    ),
};

// React Query Hooks

/**
 * Hook to fetch all available modules
 */
export function useModules() {
  return useQuery({
    queryKey: ['modules'],
    queryFn: () => modulesApi.getAllModules(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch tenant's modules
 */
export function useTenantModules(tenantId: string) {
  return useQuery({
    queryKey: ['tenants', tenantId, 'modules'],
    queryFn: () => modulesApi.getTenantModules(tenantId),
    enabled: !!tenantId,
  });
}

/**
 * Hook to enable a module for a tenant
 */
export function useEnableModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, moduleId }: { tenantId: string; moduleId: string }) =>
      modulesApi.enableModule(tenantId, moduleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.tenantId, 'modules'] });
      toast.success('Module enabled successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to enable module');
    },
  });
}

/**
 * Hook to disable a module for a tenant
 */
export function useDisableModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, moduleId }: { tenantId: string; moduleId: string }) =>
      modulesApi.disableModule(tenantId, moduleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.tenantId, 'modules'] });
      toast.success('Module disabled successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to disable module');
    },
  });
}

/**
 * Hook to configure a module
 */
export function useConfigureModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      moduleId,
      configuration,
    }: {
      tenantId: string;
      moduleId: string;
      configuration: Record<string, any>;
    }) => modulesApi.configureModule(tenantId, moduleId, configuration),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.tenantId, 'modules'] });
      toast.success('Module configured successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to configure module');
    },
  });
}
